import { useState } from "react";
import { AmazonApi } from "../../services/OpenAPI";
import { createConfiguration } from "./common";
import { isLwaNotConnectedError } from "../../utils/lwaError";

// Amazonのエラー文言から、ユーザーに分かりやすい説明を組み立てる
const buildUserMessage = (amazonMessage: string): string => {
  const base = "配送ラベルのダウンロードに失敗しました。";
  if (!amazonMessage) {
    return `${base}\n時間をおいて再度お試しください。`;
  }

  const lower = amazonMessage.toLowerCase();

  // ラベル形式（PageType）が、この配送業者・配送方法では使えないケース
  if (lower.includes("pagetype") || lower.includes("page type")) {
    return (
      `${base}\n` +
      "選択した「ラベル形式」が、この出荷の配送方法では利用できない可能性があります。\n" +
      "別の形式（例：A4 / 6面）を選んで再度お試しください。\n\n" +
      `［Amazonからの詳細］${amazonMessage}`
    );
  }

  // それ以外はAmazonの文言をそのまま添える
  return `${base}\n\n［Amazonからの詳細］${amazonMessage}`;
};

export const useApiDownloadDeliveryLabels = () => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const downloadDeliveryLabels = async (
    planId: number,
    groupNum: number,
    pageType?: string
  ): Promise<{
    success: boolean;
    errorMessage?: string;
    notConnected?: boolean;
  }> => {
    setIsLoading(true);
    setError(null);

    try {
      const config = createConfiguration();
      const api = new AmazonApi(config);

      // planId を引数で受け取り、APIを呼ぶ
      const response = await api.amazonDownloadLabelsPlanIdGroupNumGet({
        planId,
        groupNum,
        pageType,
      });

      // downloadUrl を取得し、ダウンロード処理を実行
      const downloadUrl = response.download_url;
      if (!downloadUrl) {
        throw new Error("downloadUrlが取得できませんでした");
      }

      // 自動ダウンロード処理
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return { success: true };
    } catch (err: any) {
      console.error(err);
      // 未連携は呼び出し側でユーザー名入りの案内を出すため、フラグで通知する
      if (isLwaNotConnectedError(err)) {
        return { success: false, notConnected: true };
      }
      // バックエンド経由で返るAmazonのエラー文言を取得
      const amazonMessage: string =
        err?.response?.data?.error ?? err?.message ?? "";
      const userMessage = buildUserMessage(amazonMessage);
      setError(userMessage);
      return { success: false, errorMessage: userMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return { downloadDeliveryLabels, error, isLoading };
};
